import React from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';

const SEOSchema = ({ page = 'home', portfolioItems = [] }) => {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language;
  const baseUrl = 'https://beckxfabian.be';

  // Schema pour la personne/professionnel
  const personSchema = {
    "@context": "https://schema.org/",
    "@type": "Person",
    "name": "Fabian Beck",
    "alternateName": "Beckx Fabian",
    "jobTitle": t('header.title'),
    "description": t('about.description1'),
    "url": baseUrl,
    "sameAs": [
      "https://github.com/beckxfabian",
      "https://linkedin.com/in/beckxfabian",
      "https://behance.net/beckxfabian"
    ],
    "worksFor": {
      "@type": "Organization",
      "name": "Enovatic"
    },
    "knowsAbout": [
      "Web Development",
      "UI/UX Design", 
      "Graphic Design",
      "Frontend Development",
      "React",
      "JavaScript"
    ],
    "address": {
      "@type": "PostalAddress",
      "addressCountry": "BE"
    }
  };

  // Schema pour l'organisation/entreprise
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "ProfessionalService",
    "name": "Fabian Beck - Développeur Web & Designer",
    "description": t('about.description1'),
    "url": baseUrl,
    "founder": {
      "@type": "Person",
      "name": "Fabian Beck"
    },
    "serviceType": [
      t('services.development.title'),
      t('services.design.title'),
      t('services.infography.title')
    ],
    "areaServed": {
      "@type": "Country",
      "name": "Belgium"
    }
  };

  // Schema pour le site web
  const websiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "Fabian Beck Portfolio",
    "description": t('about.description1'),
    "url": baseUrl,
    "author": {
      "@type": "Person",
      "name": "Fabian Beck"
    },
    "inLanguage": [currentLang === 'fr' ? 'fr-BE' : 'en-US'],
    "potentialAction": {
      "@type": "SearchAction",
      "target": {
        "@type": "EntryPoint",
        "urlTemplate": `${baseUrl}/#search?q={search_term_string}`
      },
      "query-input": "required name=search_term_string"
    }
  };

  // Schema pour les réalisations créatives
  const portfolioSchema = portfolioItems.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": t('portfolio.title'),
    "description": "Portfolio de réalisations créatives",
    "numberOfItems": portfolioItems.length,
    "itemListElement": portfolioItems.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "item": {
        "@type": "CreativeWork",
        "name": item.name,
        "description": item.description,
        "url": item.url,
        "image": item.image,
        "dateCreated": item.date,
        "creator": {
          "@type": "Person",
          "name": "Fabian Beck"
        },
        "genre": item.category
      }
    }))
  } : null;

  // Schema spécialisé pour page portfolio
  const creativeWorkSchema = page === 'portfolio' && portfolioItems.length > 0 ? 
    portfolioItems.map(item => ({
      "@context": "https://schema.org",
      "@type": "CreativeWork",
      "name": item.name,
      "description": item.description,
      "url": item.url,
      "image": {
        "@type": "ImageObject",
        "url": item.image
      },
      "dateCreated": item.date,
      "creator": {
        "@type": "Person",
        "name": "Fabian Beck",
        "jobTitle": t('header.title')
      },
      "genre": item.category,
      "keywords": [item.category, "design", "développement web", "créatif"]
    })) : [];

  // Breadcrumb schema
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": t('nav.home'),
        "item": baseUrl
      },
      ...(page !== 'home' ? [{
        "@type": "ListItem", 
        "position": 2,
        "name": page === 'portfolio' ? t('nav.portfolio') : 
               page === 'about' ? t('nav.about') :
               page === 'services' ? t('nav.services') :
               page === 'contact' ? t('nav.contact') : page,
        "item": `${baseUrl}#${page}`
      }] : [])
    ]
  };

  const schemas = [
    personSchema,
    organizationSchema,
    websiteSchema,
    breadcrumbSchema
  ];

  if (portfolioSchema) {
    schemas.push(portfolioSchema);
  }

  if (creativeWorkSchema.length > 0) {
    schemas.push(...creativeWorkSchema);
  }

  return (
    <Helmet>
      {schemas.map((schema, index) => (
        <script key={index} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
};

export default SEOSchema;
